import { useMemo } from 'react';

import type { PortfolioDetailedReturns } from '../../../../hooks/usePortfolioDetailedReturns';

import { Heading, cn } from '@simplywallst/ui-core/next';

import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';

import Tooltip from '@/components/Tooltip';

import { WaterfallChart } from '../WaterfallChart';

import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';

import { TransactionPortfolioUpgrade } from '../../../TransactionPortfolioUpgrade';

type Props = {
  unrealizedGains: number;
  realizedGains: number | null;
  dividends: number | null;
  currencyGains: number | null;
  totalGains: number;
  currencyISO: PortfolioDetailedReturns['currencyISO'];
  isHoldingsPortfolio?: boolean;
};

export const ReturnsBreakdownChart = ({
  unrealizedGains,
  realizedGains,
  dividends,
  currencyGains,
  totalGains,
  currencyISO,
  isHoldingsPortfolio,
}: Props) => {
  const waterfallData = useMemo(
    () => [
      [
        {
          id: 'unrealizedGains' as const,
          label: 'Unrealized',
          tip: 'Unrealized gains are the profits that have not yet been realized through a sale.',
          value: unrealizedGains,
        },
        {
          id: 'realizedGains' as const,
          label: 'Realized',
          tip: 'The gain/loss from shares sold during the selected period.',
          value: realizedGains,
        },
        {
          id: 'dividends' as const,
          label: 'Dividends',
          tip: 'Total dividends received during the selected period.',
          value: dividends,
        },
        {
          id: 'currencyGains' as const,
          label: 'Currency',
          tip: 'Foreign exchange gain/loss from shares sold during the selected period.',
          value: currencyGains,
        },
      ],
      [
        {
          id: 'total' as const,
          label: <span className="text-xs font-strong text-solid">Total</span>,
          tip: 'The overall gain/loss, including realised, unrealised, dividends, and currency effects, in the selected period.',
          value: totalGains,
        },
      ],
    ],
    [unrealizedGains, realizedGains, dividends, currencyGains, totalGains]
  );
  return (
    <div className="bg-surface-1 py-x3 md:rounded-x1 xl:px-x4">
      <div className="mb-x1 grid grid-cols-[max-content_max-content] items-center justify-between gap-x-1 px-x2 text-solid lg:px-x4 xl:px-0">
        <Heading
          as="h2"
          size="large"
          weight="strong"
          className="grid grid-cols-[max-content_24px] items-center gap-x-1"
        >
          <span>Returns Breakdown</span>
          <Tooltip
            title={
              <div className="max-w-[260px]">
                <p className="m-0 text-left text-tiny text-legacy-spacegray">
                  This waterfall chart visualises the components that make up
                  your total return.
                </p>
              </div>
            }
            light
            placement="top"
            zIndex={99999}
          >
            <InformationSolid className="fill-subtle" />
          </Tooltip>
        </Heading>
        {isHoldingsPortfolio && <TransactionPortfolioUpgrade />}
      </div>
      <div className="relative @container/waterfall">
        <div className="absolute right-0 h-full w-x3 bg-transparent bg-gradient-to-r from-transparent to-p-grey-100/80 @[432px]/waterfall:hidden sm:-right-px" />
        <div className="overflow-x-auto pb-x2 @[432px]/waterfall:pb-0">
          <div className="min-w-[432px] px-x2 lg:px-x4 xl:p-0">
            <WaterfallChart data={waterfallData}>
              {({ barProps, labelProps, value, id }) => (
                <g key={id}>
                  <WaterfallChart.Bar
                    {...barProps}
                    className={cn(
                      value === 0 || value === null
                        ? 'stroke-subtle'
                        : value > 0
                          ? 'stroke-good'
                          : 'stroke-bad'
                    )}
                  />
                  {id !== 'total' ? (
                    <WaterfallChart.Label
                      {...labelProps}
                      className="fill-soft text-xs font-strong"
                    >
                      {value === null
                        ? 'No Data'
                        : formatCurrencyToScaleTruncated(value, currencyISO)}
                    </WaterfallChart.Label>
                  ) : (
                    <WaterfallChart.Label
                      {...labelProps}
                      className={cn(
                        'fill-soft text-xs font-strong',
                        value === 0 || value === null
                          ? 'fill-soft'
                          : value > 0
                            ? 'fill-good'
                            : 'fill-bad'
                      )}
                    >
                      {formatCurrencyToScaleTruncated(value, currencyISO)}
                    </WaterfallChart.Label>
                  )}
                </g>
              )}
            </WaterfallChart>
          </div>
        </div>
      </div>
    </div>
  );
};
